import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

// Generate a session ID (one per tab)
const getSessionId = () => {
  let sid = sessionStorage.getItem("_sid");
  if (!sid) {
    sid = Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem("_sid", sid);
  }
  return sid;
};

const sessionId = getSessionId();

const UTM_KEYS = ["source", "medium", "campaign", "term", "content"];

// Client-side context sent with each hit (used for bot detection)
const getClientContext = (search) => {
  const params = new URLSearchParams(search);
  const utm = {};
  UTM_KEYS.forEach((key) => {
    const value = params.get(`utm_${key}`);
    if (value) utm[key] = value;
  });

  let timezone = "";
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (e) {}

  return {
    userAgent: navigator.userAgent,
    language: navigator.language || "",
    webdriver: navigator.webdriver === true,
    screenWidth: window.screen?.width || 0,
    screenHeight: window.screen?.height || 0,
    timezone,
    utm,
  };
};

const isExcluded = (path) =>
  path.startsWith("/dashboard") || path === "/login";

const PageTracker = () => {
  const location = useLocation();
  const startTimeRef = useRef(Date.now());
  const prevPathRef = useRef(null);

  useEffect(() => {
    // 1. Send duration of previous page
    if (prevPathRef.current && !isExcluded(prevPathRef.current)) {
      const duration = Math.round(
        (Date.now() - startTimeRef.current) / 1000
      );
      navigator.sendBeacon(
        "/api/analytics/leave",
        new Blob(
          [
            JSON.stringify({
              sessionId,
              path: prevPathRef.current,
              duration,
            }),
          ],
          { type: "application/json" }
        )
      );
    }

    // 2. Track new page in MongoDB (exclude admin/login)
    if (!isExcluded(location.pathname)) {
      axios
        .post("/api/analytics/track", {
          path: location.pathname,
          referrer: document.referrer,
          sessionId,
          ...getClientContext(location.search),
        })
        .catch(() => {});
    }

    // 3. GA4 tracking (all pages)
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: location.pathname,
        page_title: document.title,
      });
    }

    // 4. Reset timer
    startTimeRef.current = Date.now();
    prevPathRef.current = location.pathname;

    // 5. Handle tab close / navigation away
    const handleUnload = () => {
      if (!isExcluded(location.pathname)) {
        const dur = Math.round(
          (Date.now() - startTimeRef.current) / 1000
        );
        navigator.sendBeacon(
          "/api/analytics/leave",
          new Blob(
            [
              JSON.stringify({
                sessionId,
                path: location.pathname,
                duration: dur,
              }),
            ],
            { type: "application/json" }
          )
        );
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [location]);

  return null;
};

export default PageTracker;
